import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { fmtDate } from '../utils/format'
import SortableHeader, { useSort } from '../components/SortableHeader'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

const API_BASE_URL = '/api/v1/dev'

interface Allergen {
  id: number
  name: string
}

interface Ingredient {
  id: number
  name: string
  default_unit_name: string
}

interface Supplier {
  id: number
  trade_name: string
}

interface InventoryBatch {
  id: number
  ingredient_id: number
  ingredient: Ingredient
  internal_code: string
  lot_number: string
  supplier_id: number
  supplier: Supplier
  quantity_total_g: number
  quantity_available_g: number
  best_before: string | null
  expiry_date: string | null
  opened_at: string | null
  closed_at: string | null
  status: 'unopened' | 'open' | 'depleted' | 'expired'
  allergens_snapshot: Allergen[]
  location: string | null
  brand: string | null
  usage_percentage: number
  days_until_expiry: number | null
  created_at: string
}

interface PaginatedResponse {
  data: InventoryBatch[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export default function Inventory() {
  const navigate = useNavigate()
  const [batches, setBatches] = useState<InventoryBatch[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState('')
  const { sortField, sortDirection, handleSort, sortData } = useSort<InventoryBatch>('created_at', 'desc')

  // Stats
  const [stats, setStats] = useState({
    unopened: 0,
    open: 0,
    depleted: 0,
    expired: 0,
    expiring_soon: 0
  })

  const fetchBatches = async (page = 1, search = '') => {
    try {
      setError('')

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '15',
        sort_by: 'created_at',
        sort_order: 'desc'
      })

      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }

      if (search.trim()) {
        params.append('search', search.trim())
      }

      const response = await fetch(`${API_BASE_URL}/inventory/batches?${params}`)
      const data: PaginatedResponse = await response.json()

      if (response.ok) {
        setBatches(data.data)
        setCurrentPage(data.current_page)
        setTotalPages(data.last_page)
        setTotal(data.total)

        // Calculate stats
        const unopened = data.data.filter(b => b.status === 'unopened').length
        const open = data.data.filter(b => b.status === 'open').length
        const depleted = data.data.filter(b => b.status === 'depleted').length
        const expired = data.data.filter(b => b.status === 'expired').length
        const expiring_soon = data.data.filter(b =>
          b.days_until_expiry !== null && b.days_until_expiry <= 7 && b.days_until_expiry > 0
        ).length

        setStats({ unopened, open, depleted, expired, expiring_soon })
      } else {
        setError('Error al cargar los lotes')
      }
    } catch (error) {
      setError('Error de conexión')
      console.error('Error fetching batches:', error)
    } finally {
      setInitialLoading(false)
    }
  }

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      fetchBatches(1, searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm, statusFilter])

  // Fetch when page changes
  useEffect(() => {
    fetchBatches(currentPage, searchTerm)
  }, [currentPage])

  const handleOpenBatch = async (batchId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/batches/${batchId}/open`, {
        method: 'POST'
      })

      if (response.ok) {
        fetchBatches(currentPage)
      } else {
        const data = await response.json()
        setError(data.message || 'Error al abrir el lote')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } catch (error) {
      setError('Error de conexión')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleCloseBatch = async (batchId: number) => {
    if (!confirm('¿Está seguro de que desea cerrar este lote?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/inventory/batches/${batchId}/close`, {
        method: 'POST'
      })

      if (response.ok) {
        fetchBatches(currentPage)
      } else {
        const data = await response.json()
        setError(data.message || 'Error al cerrar el lote')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  // Search is now handled by the backend API

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unopened':
        return 'bg-blue-100 text-blue-800'
      case 'open':
        return 'bg-green-100 text-green-800'
      case 'depleted':
        return 'bg-gray-100 text-gray-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'unopened':
        return 'Sin abrir'
      case 'open':
        return 'Abierto'
      case 'depleted':
        return 'Agotado'
      case 'expired':
        return 'Expirado'
      default:
        return status
    }
  }

  const formatWeight = (grams: number) => {
    if (grams >= 1000) {
      return `${(grams / 1000).toFixed(2)} kg`
    }
    return `${Math.round(grams)} g`
  }

  const getExpiryWarning = (days: number | null) => {
    if (days === null) return null
    if (days < 0) return { color: 'text-red-600', icon: ExclamationTriangleIcon, text: 'Expirado' }
    if (days === 0) return { color: 'text-red-600', icon: ExclamationTriangleIcon, text: 'Expira hoy' }
    if (days <= 3) return { color: 'text-red-600', icon: ExclamationTriangleIcon, text: `${days} días` }
    if (days <= 7) return { color: 'text-orange-600', icon: ClockIcon, text: `${days} días` }
    if (days <= 14) return { color: 'text-yellow-600', icon: ClockIcon, text: `${days} días` }
    if (days <= 30) return { color: 'text-green-600', icon: ClockIcon, text: `${days} días` }
    return { color: 'text-gray-500', icon: ClockIcon, text: `${days} días` }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-gray-900">Inventario de Lotes</h1>
            <p className="mt-1 text-sm text-gray-600">
              Gestión de lotes de ingredientes con trazabilidad completa
            </p>
          </div>
          <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
            <button
              type="button"
              onClick={() => navigate('/inventory-by-ingredient')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="-ml-1 mr-2 h-5 w-5" />
              Por Ingrediente
            </button>
            <button
              type="button"
              onClick={() => navigate('/purchases/receiving')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Recepción de Mercancía
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">{stats.unopened}</span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Sin abrir</dt>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">{stats.open}</span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Abiertos</dt>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gray-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">{stats.depleted}</span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Agotados</dt>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">{stats.expiring_soon}</span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Por caducar</dt>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">{stats.expired}</span>
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Expirados</dt>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Buscar por ingrediente, lote o proveedor..."
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 rounded-md"
              >
                <option value="all">Todos los estados</option>
                <option value="unopened">Sin abrir</option>
                <option value="open">Abiertos</option>
                <option value="depleted">Agotados</option>
                <option value="expired">Expirados</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Batches Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Lotes en Inventario ({total})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Código/Lote" field="internal_code" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Ingrediente" field="ingredient.name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Marca/Alias" field="brand" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Proveedor" field="supplier.trade_name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Cantidad" field="quantity_available_g" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Estado" field="status" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Caducidad" field="expiry_date" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alérgenos
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortData(batches).map((batch) => {
                const expiryWarning = getExpiryWarning(batch.days_until_expiry)
                const WarningIcon = expiryWarning?.icon

                return (
                  <tr key={batch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-gray-900">
                        {batch.internal_code}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">{batch.lot_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {batch.ingredient?.name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {batch.brand || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {batch.supplier?.trade_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatWeight(batch.quantity_available_g)} / {formatWeight(batch.quantity_total_g)}
                      </div>
                      <div className="w-32 bg-gray-200 rounded-full h-2 mt-1">
                        {(() => {
                          const remaining = batch.quantity_total_g > 0
                            ? (batch.quantity_available_g / batch.quantity_total_g) * 100
                            : 0
                          const color = remaining >= 50 ? 'bg-green-500' : remaining >= 25 ? 'bg-orange-500' : 'bg-red-500'
                          return (
                            <div
                              className={`${color} h-2 rounded-full`}
                              style={{ width: `${Math.min(100, Math.max(0, remaining))}%` }}
                            ></div>
                          )
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(batch.status)}`}>
                        {getStatusText(batch.status)}
                      </span>
                      {batch.opened_at && (
                        <div className="text-xs text-gray-500 mt-1">
                          Abierto: {fmtDate(batch.opened_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(batch.best_before || batch.expiry_date) && (
                        <div>
                          <div className="text-sm text-gray-600">
                            {fmtDate(batch.best_before || batch.expiry_date!)}
                          </div>
                          {expiryWarning && WarningIcon && (
                            <div className={`flex items-center text-xs ${expiryWarning.color} mt-1`}>
                              <WarningIcon className="h-4 w-4 mr-1" />
                              {expiryWarning.text}
                            </div>
                          )}
                        </div>
                      )}
                      {!batch.best_before && !batch.expiry_date && <span className="text-sm text-gray-400">N/A</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {batch.allergens_snapshot && batch.allergens_snapshot.length > 0 ? (
                          batch.allergens_snapshot.slice(0, 3).map((allergen) => (
                            <span
                              key={allergen.id}
                              className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                            >
                              {allergen.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Sin alérgenos</span>
                        )}
                        {batch.allergens_snapshot && batch.allergens_snapshot.length > 3 && (
                          <span className="text-xs text-gray-500">+{batch.allergens_snapshot.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {batch.status === 'unopened' && (
                        <button
                          onClick={() => handleOpenBatch(batch.id)}
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          Abrir
                        </button>
                      )}
                      {batch.status === 'open' && (
                        <button
                          onClick={() => handleCloseBatch(batch.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Cerrar
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/inventory/${batch.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeIcon className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
